import { IAPIMetadataItem } from 'library/ts/interfaces/metadata/api/IAPIMetadataItem';
import { AssetEmbedType } from 'library/ts/model/enums/content/AssetEmbedType';
import { MPXAvailableProfileType } from 'library/ts/model/enums/mpx/MPXAvailableProfileType';
import { MPXMediaType } from 'library/ts/model/enums/mpx/MPXMediaType';
import { MPXVideoProperty } from 'library/ts/model/enums/mpx/MPXVideoProperty';
import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { ITag } from 'tag/ts/tag/interfaces/ITag';
import { IParamsVO } from 'tag/ts/tag/interfaces/params/IParamsVO';
import { ITagContentUtilities } from 'tag/ts/tag/interfaces/utilities/ITagContentUtilities';
import { Manifest } from 'tag/ts/tag/model/type/Manifest';

/**
 * @copyright © Paramount Streaming
 * @author  Stan Gletski
 * @description Content utilities for specific use cases (i.e. DAI related etc.)
 */
export class TagContentUtilities extends AbstractTagClass implements ITagContentUtilities {
	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				...argument,
				name: 'TagContentUtilities'
			}
		);
	}

	/**
	 * @description Returns asset type set by the page in paramsVO.CONTENT
	 * @param {IParamsVO} params
	 * @return {AssetEmbedType}
	 */
	assetEmbedType = ( params: IParamsVO = this.params ): AssetEmbedType => {
		// counter for number if assets set in Smart Tag paramsVO.CONTENT, only one can be set
		let assetsSet = 0;
		// loop over asset types possibly set in Smart Tag params
		[ AssetEmbedType.PID, AssetEmbedType.CID, AssetEmbedType.ASSET_URL ].forEach(
			( assetType ) => {
				if ( !this.utils.Type.isEmpty( params?.CONTENT[ assetType ] ) ) {
					assetsSet++;
				}
			}
		);

		// if more than one asset type set => return undefined
		if ( assetsSet > 1 ) {
			return undefined;
		}

		if ( this.utils.Type.isString( params?.CONTENT?.pid ) && !this.utils.Type.isEmpty( params?.CONTENT?.pid ) ) {
			return AssetEmbedType.PID;
		}

		if ( this.utils.Type.isString( params?.CONTENT?.contentId ) && !this.utils.Type.isEmpty( params?.CONTENT?.contentId ) ) {
			return AssetEmbedType.CID;
		}

		if ( this.utils.Type.isString( params.CONTENT?.assetURL ) && !this.utils.Type.isEmpty( params?.CONTENT?.assetURL ) ) {
			return AssetEmbedType.ASSET_URL;
		}

		return undefined;
	};

	/**
	 * @description Returns a boolean flag for assetURL live stream or DAI live stream
	 * @param {IParamsVO} params
	 * @param {IAPIMetadataItem} metadata
	 * @return {boolean}
	 */
	isLive = (
		params: IParamsVO = this.params,
		metadata: IAPIMetadataItem = this?.tag?.model?.apiMetadata
	): boolean => {
		if (
			// Syncbak Live stream => Local TV stations
			// Non-empty assetURL+ isLive === true
			(
				this.utils.Type.isString( params?.CONTENT?.assetURL )
				&&
				this.utils.Type.isEmpty( params?.CONTENT?.assetURL ) === false
				&&
				params?.CONTENT?.isLive === true
			)
			||
			// MPX Media Type is 'Live'
			(
				metadata?.mediaType === MPXMediaType.LIVE
			)
			||
			// MPX videoProperties contains 'DAI Live Stream' value
			(
				this.utils.Type.isArray( metadata?.videoProperties )
				&&
				metadata?.videoProperties.includes( MPXVideoProperty.DAI_LIVE_STREAM )
			)
		) {
			// return true
			return true;
		}

		// Otherwise, return whatever value was passed to Smart Tag from the page in paramsVO
		return params?.CONTENT?.isLive;
	};

	/**
	 * @description Do we have DAI Stream Key in MPX Metadata?
	 * @param {IParamsVO} params
	 * @param {IAPIMetadataItem} metadata
	 * @returns {boolean}
	 */
	hasDAIAssetKey = (
		params: IParamsVO = this.params,
		metadata: IAPIMetadataItem = this?.tag?.model?.apiMetadata
	): boolean => {
		// pull assetKey from params and daistreamKey from video metadata, so we can check if DAI SDK is required
		const assetKey: string | undefined = this.getDAIAssetKey(
			params,
			metadata
		);
		return (
			this.utils.Type.isString( assetKey ) &&
			!this.utils.Type.isEmpty( assetKey )
		);
	};

	/**
	 * @description Returns DAI asset / stream key
	 * @param {IParamsVO} params
	 * @param {IAPIMetadataItem} metadata
	 * @return {string | undefined}
	 */
	getDAIAssetKey = (
		params: IParamsVO = this.params,
		metadata: IAPIMetadataItem = this?.tag?.model?.apiMetadata
	) => {
		// DAI asset key from API Metadata
		const assetKey = metadata?.daistreamKey;
		// site override of DAI asset Key
		// if ( this.utils.Type.isEmpty( assetKey ) ) assetKey = params?.CONTENT?.resource?.ad?.ssai?.assetKey;
		// sanity check
		if ( this.utils.Type.isEmpty( assetKey ) ) return undefined;
		return assetKey;
	};

	/**
	 * @description Do we have DAI Live Stream in MPX Metadata videoProperties array?
	 * @param {IAPIMetadataItem} metadata
	 * @returns {boolean}
	 */
	isLiveDAIStream = (
		metadata: IAPIMetadataItem = this?.tag?.model?.apiMetadata
	): boolean => {
		const videoProperties = metadata?.videoProperties ?? metadata?.contentCANVideo?.videoProperties;
		return Array.isArray( videoProperties ) && videoProperties.includes( MPXVideoProperty.DAI_LIVE_STREAM );
	};

	/**
	 * @description Do we have PID content type?
	 * @param {ITag} tag
	 * @returns {boolean}
	 */
	isPID = ( tag: ITag = this.tag ): boolean => {
		// pull pid from params from video metadata so we can check if DAI API is required
		const pid: string = tag.params?.CONTENT?.pid || tag.model.apiMetadata?.pid;
		return this.utils.Type.isString( pid ) && !this.utils.Type.isEmpty( pid ); // pid is present and not empty
	};

	/**
	 * @description Do we have assetURL content type?
	 * @param {ITag} tag
	 * @returns {boolean}
	 */
	isAssetURL = ( tag: ITag = this.tag ): boolean => {
		// pull pid from params from video metadata, so we can check if DAI API is required
		const assetURL: string = tag.params?.CONTENT?.assetURL;
		return (
			this.utils.Type.isString( assetURL ) &&
			!this.utils.Type.isEmpty( assetURL )
		); // assetURL is present and not empty
	};

	/**
	 * @description Checks if asset is DAI and returns boolean
	 * @param {IParamsVO} params
	 * @param {IAPIMetadataItem} metadata
	 * @returns {boolean}
	 */
	isDAIAsset = (
		params: IParamsVO = this.params,
		metadata: IAPIMetadataItem = this?.tag?.model?.apiMetadata
	): boolean => {
		let isDAI: boolean = false;
		// filter per asset type
		switch ( this.assetEmbedType( this.params ) ) {
			case AssetEmbedType.ASSET_URL:
				// Check assetURL for DAI pattern
				isDAI = this.daiAssetURL( params?.CONTENT?.assetURL );
				break;
			case AssetEmbedType.PID:
				isDAI = this.daiMetadata( metadata );
				break;
		}
		// return result
		return isDAI;
	};

	/**
	 * @description Is it a Live video according to MPX mediaType?
	 * @param {IAPIMetadataItem} metadata
	 * @returns {boolean}
	 */
	isMediaTypeLiveVideo = (
		metadata: IAPIMetadataItem = this?.tag?.model?.apiMetadata
	): boolean => {
		return (
			this.utils.Type.isString( metadata.mediaType ) &&
			metadata.mediaType.toLowerCase() === MPXMediaType.LIVE.toLowerCase()
		);
	};

	/**
	 * @description Is our asset DAI by asset URL patterns?
	 * @param {string} assetURL
	 * @returns {boolean}
	 */
	daiAssetURL = ( assetURL: string ): boolean => {
		// check if assetURL is not empty + it has DAI pattern
		return (
			!this.utils.Type.isEmpty( assetURL ) &&
			this.utils.URL.parser( assetURL ).host.indexOf( 'dai.google.com' ) > -1
		);
	};

	/**
	 * @description Is our asset DAI by MPX metadata criteria?
	 * @param {IAPIMetadataItem} metadata
	 * @returns {boolean}
	 */
	daiMetadata = ( metadata: IAPIMetadataItem ): boolean => {
		let isDAI: boolean = false;

		// check if metadata is undefined or an emp[ty object
		switch ( this.utils.Type.isEmpty( metadata ) ) {
			case true: // undefined || empty object
				isDAI = false;
				break;
			case false: // defined and not an empty object
				// check DAI criteria in MPX metadata
				if (
					this.utils.Type.isObject( metadata ) && // if we have MPX metadata
					this.utils.Type.isArray( metadata.videoProperties ) && // and it has videoProperties array property
					metadata.videoProperties.includes(
						MPXVideoProperty.DAI_LIVE_STREAM
					) // and it contains value indicating DAI
				) {
					isDAI = true;
				}
				break;
		}
		return isDAI;
	};

	/**
	 * @description Returns manifest extension for asset URL
	 * @param {string} url
	 * @return {Manifest | string}
	 */
	assetManifestExtension = ( url: string = '' ): Manifest | string => {
		return this.utils.URL.getFileExtension(
			this.utils.URL.parser( url ).pathname
		) as Manifest;
	};

	/**
	 * @description Returns partner id
	 * @param {IParamsVO} params
	 * @return {string}
	 */
	getPartnerID = ( params: IParamsVO = this.params ): string => {
		if ( this.utils.Type.isNullOrUndefined( params?.CONTENT?.partner ) ) {
			this?.logger?.log( 'getPartnerID', 'returning params.CONTENT.partner:', params?.CONTENT?.partner );
			return params.CONTENT.partner;
		} else {
			this?.logger?.log( 'getPartnerID', 'params.CONTENT.partner is unset... returning fallback constant: cbs' );
			return 'cbs';
		}
	};

	/**
	 * @description Returns DRM status of current video
	 * @param {IParamsVO} params
	 * @param {IAPIMetadataItem} metadata
	 * @return {boolean}
	 */
	isDRM = (
		params: IParamsVO = this.params,
		metadata: IAPIMetadataItem = this?.tag?.model?.apiMetadata
	): boolean => {
		// filter per drm property existence
		switch ( this.utils.Type.isEmpty( params?.CONTENT?.drm ) ) {
			case true: // drm object is empty or undefined / null
				return false;
			case false: // drm object is defined
				return true;
		}
		// TODO: check each property of params.CONTENT.drm if drm is not an empty object ?
		// TODO: check apiMetadata.assetType on top of params.CONTENT.drm check ?
		// default output
		return false;
	};

	/**
	 * @description Checks if Live stream is local live channel stream => syncbak
	 * @param {IParamsVO} params
	 * @param {IAPIMetadataItem} metadata
	 * @returns {boolean}
	 */
	isLocalLiveAffiliate = (
		params: IParamsVO = this.params,
		metadata: IAPIMetadataItem = this?.tag?.model?.apiMetadata
	): boolean => {
		// if live asset url
		if ( this.isLive( params, metadata ) && this.assetEmbedType( params ) === AssetEmbedType.ASSET_URL ) {
			// is syncbak?
			return ( this.utils.URL.parser( this.params.CONTENT.assetURL ).host.indexOf( '.syncbak.com' ) > -1 );
		}
		// default output
		return false;
	};

	/**
	 * @description Returns true when asset is linear ad trackable i.e. UFC stream
	 * IMPORTANT: `isLinearAdTrackable()` does NOT imply AdSetupDefinition.LINEAR_AD_TRACKING`
	 * For example, CLID3 + DAI streams may return true while using AdSetupDefinition.DAI_*
	 * Use `adUtilities.adSetupInfo().setup.definition` to determine the active ad setup.
	 * @param {IParamsVO} params
	 * @param {IAPIMetadataItem} metadata
	 * @returns {boolean}
	 */
	isLinearAdTrackable = (
		params: IParamsVO = this.params,
		metadata: IAPIMetadataItem = this?.tag?.model?.apiMetadata
	): boolean => {
		const videoProperties = metadata?.videoProperties ?? metadata?.contentCANVideo?.videoProperties;
		return Array.isArray( videoProperties ) && videoProperties.includes( MPXVideoProperty.LINEAR_AD_TRACKING_PLUGIN );
	};

	/* @description Returns a boolean flag for kids content
	 * @param {IAPIMetadataItem} metadata
	 * @returns {boolean}
	 */
	isKidsContent = ( metadata: IAPIMetadataItem = this?.tag?.model?.apiMetadata ) => {
		const {
			KIDS,
			YOUNGER_KIDS
		} = MPXAvailableProfileType;
		const videoProperties = metadata?.videoProperties;
		const genre = metadata?.genre;
		const availableForProfileTypes = metadata?.availableForProfileTypes;
		const kids = availableForProfileTypes?.includes( KIDS ) === true;
		const youngerKids = availableForProfileTypes?.includes( YOUNGER_KIDS ) === true;
		const profileKidsContent = kids || youngerKids;
		const isPlutoKidsContent = metadata?.isKidsContent === true;
		const isKidsContent = videoProperties?.includes( MPXVideoProperty.KIDS ) === true;
		const kidsGenre =
			genre?.toLowerCase() === 'kids' ||
			genre?.toLowerCase() === 'kids and family';
		return isPlutoKidsContent || isKidsContent || kidsGenre || profileKidsContent;
	};
}